/**
 * B4 (E-1.C.2-04): Usage Dashboard
 * SMS credits, email vol, AI tokens, storage, contacts.
 * Alert at 80% threshold. Auto-recharge config.
 */
import { useState, useEffect } from "react";
import { useTranslation } from "@/i18n";
import { apiFetch } from "@/lib/api-client";
import { formatNumber } from "@/lib/i18n";
import { cn } from "@/lib/utils";

interface UsageMeter {
  key: string;
  label: string;
  current: number;
  limit: number;
  unit: string;
  alertThreshold: number; // percentage (0-100)
}

const USAGE_THRESHOLD_PCT = 80;

export default function UsageDashboardPage() {
  const { t } = useTranslation();
  const [meters, setMeters] = useState<UsageMeter[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    apiFetch<{ meters: UsageMeter[] }>("/platform/billing/usage")
      .then((res) => setMeters(res.meters))
      .catch(() => {
        setMeters([
          { key: "contacts", label: t("billing.usage.contacts"), current: 0, limit: 500, unit: "", alertThreshold: USAGE_THRESHOLD_PCT },
          { key: "emails", label: t("billing.usage.emails"), current: 0, limit: 1000, unit: "/mo", alertThreshold: USAGE_THRESHOLD_PCT },
          { key: "sms", label: t("billing.usage.sms"), current: 0, limit: 0, unit: "credits", alertThreshold: USAGE_THRESHOLD_PCT },
          { key: "ai_tokens", label: t("billing.usage.aiTokens"), current: 0, limit: 0, unit: "tokens", alertThreshold: USAGE_THRESHOLD_PCT },
          { key: "storage", label: t("billing.usage.storage"), current: 0, limit: 500, unit: "MB", alertThreshold: USAGE_THRESHOLD_PCT },
        ]);
      })
      .finally(() => setIsLoading(false));
  }, [t]);

  if (isLoading) {
    return (
      <div className="grid gap-4 md:grid-cols-3">
        {Array.from({ length: 5 }).map((_, i) => <div key={i} className="h-32 animate-pulse rounded-lg bg-muted" />)}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h2 className="text-lg font-semibold text-foreground">{t("billing.usage.title")}</h2>

      <div className="grid gap-4 md:grid-cols-3">
        {meters.map((meter) => {
          const pct = meter.limit > 0 ? Math.round((meter.current / meter.limit) * 100) : 0;
          const isWarning = pct >= meter.alertThreshold;
          const isOver = pct >= 100;

          return (
            <div key={meter.key} className={cn("rounded-lg border p-4", isOver ? "border-[var(--color-destructive)]/30 bg-[var(--color-destructive)]/5" : isWarning ? "border-[var(--color-warning)]/30 bg-[var(--color-warning)]/5" : "border-border bg-card")}>
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-foreground">{meter.label}</p>
                {isWarning && !isOver && <span className="text-xs text-[var(--color-warning)]">⚠️ {pct}%</span>}
                {isOver && <span className="text-xs text-[var(--color-destructive)]">🚨 {t("billing.usage.limitReached")}</span>}
              </div>
              <div className="mt-2 flex items-baseline gap-1">
                <span className="text-2xl font-bold text-foreground">{formatNumber(meter.current)}</span>
                {meter.limit > 0 && (
                  <span className="text-sm text-muted-foreground">/ {formatNumber(meter.limit)} {meter.unit}</span>
                )}
                {meter.limit === 0 && meter.unit && (
                  <span className="text-sm text-muted-foreground">{meter.unit}</span>
                )}
              </div>
              {meter.limit > 0 && (
                <div className="mt-2 h-2 rounded-full bg-muted">
                  <div
                    className={cn("h-2 rounded-full transition-all", isOver ? "bg-[var(--color-destructive)]/50" : isWarning ? "bg-[var(--color-warning)]/50" : "bg-primary")}
                    style={{ width: `${Math.min(pct, 100)}%` }}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
